import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { AUTH_SESSION_HEADER } from '../auth/auth-session';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly publicRoutes = [
    'novibet/transaction-details',
    'novibet/transaction-withdrawal-details',
    'service-location-reports',
    'agentlogin',
    'login',
    'resetpassword',
    'forgot-password',
    'changepassword',
    'agentchangepassword',
    '404'
  ];

  constructor(private authService: AuthService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(req, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    const requestSessionId = req.headers.get(AUTH_SESSION_HEADER);
    // A delayed request from a previous login must not be retried with the new
    // account's credentials or cause the new account to be logged out.
    if (!requestSessionId || requestSessionId !== this.authService.getSessionId()) {
      return throwError(() => originalError);
    }
    const currentUrl = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '');
    const isPublic = this.publicRoutes.some(route => currentUrl.includes(route));
    const isRefreshRequest = req.url.includes('refresh-token');
    const isLogoutRequest = req.url.includes('logout');
    const isRefreshSkipped = req.headers.has('X-Skip-Refresh');
    const isAlreadyRetried = req.headers.has('X-Token-Retry');
    const hasToken = !!this.authService.getToken();

    // If public route, refresh endpoint itself, or request already retried once, do NOT refresh
    if (isPublic || isRefreshRequest || isLogoutRequest || isRefreshSkipped || isAlreadyRetried || !hasToken) {
      if (!isPublic && isRefreshRequest && hasToken) {
        this.authService.logout();
      } else if (!isPublic && isAlreadyRetried && hasToken) {
        this.authService.triggerSessionWarning();
      }
      return throwError(() => originalError);
    }

    // Attempt single-flight JWT recovery
    return this.authService.refreshSession().pipe(
      switchMap(({ token, csrfToken }) => {
        if (this.authService.getSessionId() !== requestSessionId) {
          return throwError(() => originalError);
        }
        // Clone original request with new tokens and retry marker to prevent infinite loops
        const retriedReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-TOKEN': csrfToken,
            'X-Token-Retry': 'true'
          }
        });
        return next.handle(retriedReq);
      }),
      catchError((_refreshErr) => {
        // Refresh failed: clear auth state once and propagate the original error.
        if (this.authService.getSessionId() === requestSessionId) {
          this.authService.logout();
        }
        return throwError(() => originalError);
      })
    );
  }
}

